// Dialog zum Anlegen/Bearbeiten eines Katalog-Mailings (DCP-2228 Folge-Feature)
//
// Die Bezeichnung bleibt beim Bearbeiten editierbar — anders als die E-Mail im
// UserFormDialog, die dort gesperrt ist. Der Unterschied ist beabsichtigt: eine
// E-Mail ist eine externe Identität, eine Mailing-Bezeichnung ein Anzeigename, den
// der Fachbereich korrigieren darf. Das Backend löst eine Umbenennung als
// Neu-Anlegen + Alt-Löschen; der Hinweis unter dem Feld sagt das, damit niemand
// eine stille Nebenwirkung erlebt.
//
// Nicht hier geprüft wird die bereinigte Schlüssel-Kollision („JAN A" vs.
// „JAN_A"): dafür müsste toMailingKey() im Browser nachgebaut werden, also eine
// zweite Implementierung derselben commercetools-Eigenheit. Die kommt vom Server
// als 409 und wird unter dem Feld angezeigt.
//
// Zeitraum 2 ist bewusst optional: die meisten Mailings bewerben nur einen
// Zeitraum, nur einzelne Kategorien mit längerer Vorlaufzeit brauchen einen
// zweiten. Onlinestellung/Eingabe/Versand werden NICHT gegeneinander geprüft —
// die reale Planungstabelle enthält eine Ausgabe, bei der die Eingabefrist nach
// der Onlinestellungsfrist liegt, und das ist Sache des Fachbereichs, nicht
// dieses Formulars.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import type { Mailing, SaveMailingRequest } from '@/types';

interface Props {
  open: boolean;
  /** null = anlegen; sonst bearbeiten. */
  mailing: Mailing | null;
  /** Bezeichnungen aller Mailings — für die Doppelt-Prüfung ohne Rundreise. */
  existingTitles: string[];
  /** Serverfehler (z.B. 409 Kollision), erscheint im Dialog statt als Toast. */
  saveError?: string | null;
  onOpenChange: (open: boolean) => void;
  onSave: (req: SaveMailingRequest) => void;
  isSaving?: boolean;
}

export interface MailingFormValues {
  title: string;
  period1From: string;
  period1To: string;
  period2From: string;
  period2To: string;
  onlineDate: string;
  cutoffDate: string;
  deliveryDate: string;
}

const emptyForm = (): MailingFormValues => ({
  title: '',
  period1From: '',
  period1To: '',
  period2From: '',
  period2To: '',
  onlineDate: '',
  cutoffDate: '',
  deliveryDate: '',
});

/** Die Fehler, die diese Prüfung melden kann — als i18n-Schlüssel. */
export type MailingFormError =
  | 'mailings.form.errors.title'
  | 'mailings.form.errors.dates'
  | 'mailings.form.errors.period1Order'
  | 'mailings.form.errors.period2Incomplete'
  | 'mailings.form.errors.period2Order'
  | 'mailings.form.errors.duplicate';

/**
 * Prüft das Formular und gibt einen i18n-Schlüssel zurück, oder null.
 *
 * Spiegelt die Serverregeln, damit der häufige Fehler keine Rundreise braucht.
 * `previousTitle` schließt die bearbeitete Zeile von der Doppelt-Prüfung aus —
 * sonst kollidiert jeder Eintrag beim Speichern mit sich selbst.
 */
export function validateMailingForm(
  values: MailingFormValues,
  existingTitles: readonly string[],
  previousTitle?: string
): MailingFormError | null {
  const title = values.title.trim();

  if (title.length === 0) return 'mailings.form.errors.title';

  if (
    !values.period1From ||
    !values.period1To ||
    !values.onlineDate ||
    !values.cutoffDate ||
    !values.deliveryDate
  ) {
    return 'mailings.form.errors.dates';
  }

  // Reine Tagesvergleiche als Text: alle Werte sind ISO-Tage, damit ist die
  // lexikografische Ordnung die zeitliche — und keine Zeitzone im Spiel.
  if (values.period1From > values.period1To) {
    return 'mailings.form.errors.period1Order';
  }

  const hasPeriod2From = values.period2From.length > 0;
  const hasPeriod2To = values.period2To.length > 0;
  if (hasPeriod2From !== hasPeriod2To) {
    return 'mailings.form.errors.period2Incomplete';
  }
  if (hasPeriod2From && hasPeriod2To && values.period2From > values.period2To) {
    return 'mailings.form.errors.period2Order';
  }

  if (existingTitles.some((existing) => existing === title && existing !== previousTitle)) {
    return 'mailings.form.errors.duplicate';
  }

  return null;
}

export function MailingFormDialog({
  open,
  mailing,
  existingTitles,
  saveError,
  onOpenChange,
  onSave,
  isSaving,
}: Props) {
  const { t } = useTranslation('settings');
  const [form, setForm] = useState<MailingFormValues>(emptyForm);
  const [error, setError] = useState<MailingFormError | null>(null);

  const isEdit = mailing !== null;

  useEffect(() => {
    if (!open) return;
    setForm(
      mailing
        ? {
            title: mailing.title,
            period1From: mailing.period1From,
            period1To: mailing.period1To,
            period2From: mailing.period2From ?? '',
            period2To: mailing.period2To ?? '',
            onlineDate: mailing.onlineDate,
            cutoffDate: mailing.cutoffDate,
            deliveryDate: mailing.deliveryDate,
          }
        : emptyForm()
    );
    setError(null);
  }, [open, mailing]);

  // Nur bei einer echten Umbenennung zeigen, nicht schon beim Öffnen.
  const isRenaming = isEdit && form.title.trim().length > 0 && form.title.trim() !== mailing.title;

  const handleSubmit = () => {
    const problem = validateMailingForm(form, existingTitles, mailing?.title);
    if (problem) {
      setError(problem);
      return;
    }

    setError(null);
    onSave({
      previousTitle: mailing?.title,
      title: form.title.trim(),
      period1From: form.period1From,
      period1To: form.period1To,
      period2From: form.period2From || undefined,
      period2To: form.period2To || undefined,
      onlineDate: form.onlineDate,
      cutoffDate: form.cutoffDate,
      deliveryDate: form.deliveryDate,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t(isEdit ? 'mailings.form.titleEdit' : 'mailings.form.titleCreate')}
          </DialogTitle>
          <DialogDescription>{t('mailings.form.description')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-xs text-muted-foreground" htmlFor="mailing-title">
              {t('mailings.form.fields.title')}
            </label>
            <Input
              id="mailing-title"
              value={form.title}
              onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              placeholder="JAN A"
            />
            {isRenaming && (
              <p className="text-xs text-amber-700">{t('mailings.form.renameHint')}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-period1-from">
                {t('mailings.form.fields.period1From')}
              </label>
              <Input
                id="mailing-period1-from"
                type="date"
                value={form.period1From}
                onChange={(e) => setForm((prev) => ({ ...prev, period1From: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-period1-to">
                {t('mailings.form.fields.period1To')}
              </label>
              <Input
                id="mailing-period1-to"
                type="date"
                value={form.period1To}
                onChange={(e) => setForm((prev) => ({ ...prev, period1To: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-period2-from">
                {t('mailings.form.fields.period2From')}
              </label>
              <Input
                id="mailing-period2-from"
                type="date"
                value={form.period2From}
                onChange={(e) => setForm((prev) => ({ ...prev, period2From: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-period2-to">
                {t('mailings.form.fields.period2To')}
              </label>
              <Input
                id="mailing-period2-to"
                type="date"
                value={form.period2To}
                onChange={(e) => setForm((prev) => ({ ...prev, period2To: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-online">
                {t('mailings.form.fields.onlineDate')}
              </label>
              <Input
                id="mailing-online"
                type="date"
                value={form.onlineDate}
                onChange={(e) => setForm((prev) => ({ ...prev, onlineDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-cutoff">
                {t('mailings.form.fields.cutoffDate')}
              </label>
              <Input
                id="mailing-cutoff"
                type="date"
                value={form.cutoffDate}
                onChange={(e) => setForm((prev) => ({ ...prev, cutoffDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-muted-foreground" htmlFor="mailing-delivery">
                {t('mailings.form.fields.deliveryDate')}
              </label>
              <Input
                id="mailing-delivery"
                type="date"
                value={form.deliveryDate}
                onChange={(e) => setForm((prev) => ({ ...prev, deliveryDate: e.target.value }))}
              />
            </div>
          </div>

          {/* Eigene Prüfung zuerst, danach die Servermeldung — die ist spezifischer
              (z.B. die Kollisionsregel) und soll nicht von ihr verdeckt werden. */}
          {error && <p className="text-sm text-red-600">{t(error)}</p>}
          {!error && saveError && <p className="text-sm text-red-600">{saveError}</p>}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('mailings.form.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSaving}>
            {isSaving ? t('mailings.form.saving') : t('mailings.form.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
